import { PsHttpService, type PatSnapSuccessResponseData } from '@patsnap/tahiti-core/common/ps-http';
import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import { FactoryPsHttpSSE as FactoryPsHttpSSEOfficial, PsHttpSSE as PsHttpSSEOfficial } from '@patsnap-utils/sse';
import {
    FtoProApi,
    type ClaimAnalysisResult,
    type CustomSearchRequest,
    type DialogueStreamRequest,
    type ExtractFeatureRequest,
    type FtoSSEMessage,
    type FTOScoreRequest,
    type FTOScoreResponse,
    type NextStepRequest,
    type NextStepResponse,
    type PatentFilterContext,
    type PatentFilterToolRequest,
    type PatentFinalResultWithClaimAnalysis,
    type PatentListRequest,
    type PatentListResponse,
    type PatentSearchAnalyzeRequest,
    type PatentSearchResponse,
    type PatentSearchToolRequest,
    type ImportFromWorkspaceResponse,
    type WorkspaceInfo,
    type WorkspaceInfoResponse,
    type ProcessAddingPatentRequest,
    type ExportReportAsWordResponse,
    type CancelTaskRequest,
    type CancelTaskResponse,
    type ResumeTaskRequest,
    type ResumeTaskResponse,
    type HelperSectionResponse,
    type HelperSubclassRequest,
    type HelperSubclassResponse,
    type HelperSearchRequest,
    type HelperSearchResponse,
    type UploadImagesResponse,
    type UploadProgressCallback,
    type SignFilesResponse,
    type WorkspaceFolderPatentsRequest,
} from '../fto-pro-api';
import type { ExtractFeatureResult } from '../types/extract-feature-result';

import { ftoProApiUrlPrefix, coreSearchApiUrlPrefix, coreTelecomApiUrlPrefix } from '@telecom/shared/domain-config';
import type { CreateTaskRequest, CreateTaskResponse, TaskExistsRequest, TaskExistsResponse } from '../types/task';
import type { ExpandedKeywordsRequest, ExpandedKeywordsResponse, CompleteKeywordsRequest, CompleteKeywordsResponseData } from '../types/expanded-keywords';
import type { FtoProjectRequest, FtoProjectResponse } from '../types/fto-project';
import type { RetrievalQueriesResponse } from '../types/patent-query';
import type { FtoReportResponse, FtoReportListRequest, FtoReportListResponse } from '../types/fto-report';
import type { PatentCountResponse } from '../types/patent-count';
import type {
    SemanticRankingPatentPageRequest,
    SemanticRankingPatentPageResponse,
    TechnicalTopicPatentPageRequest,
    TechnicalTopicPatentPageResponse,
    TechnicalTopicThemeListRequest,
    TechnicalTopicThemeListResponse,
} from '../types/patent-screening';
import { isUserLogin } from '@telecom/shared/startup/user-auth';

const useLocalFtoProApi = !!import.meta.env.VITE_USE_LOCAL_FTO_PRO_API;
const isDev = import.meta.env.DEV;
const localFtoProApiUrlPrefix = 'http://127.0.0.1:8080/fto_pro';


/**
 * FTO Pro API 服务实现类
 * @description 实现所有 API 接口的真实调用
 */
@injectable()
export class FtoProApiServiceImpl extends PsHttpService
    implements
        FtoProApi {

    @inject(FactoryPsHttpSSEOfficial)
    private _factorySsePsHttp!: FactoryPsHttpSSEOfficial;

    /**
     * HTTP 服务配置选项
     */
    get options() {
        return {
            baseURL: isDev && useLocalFtoProApi ? localFtoProApiUrlPrefix : coreTelecomApiUrlPrefix,
            headers: {
                'X-API-Version': '2.0',
            },
        };
    }

    /**
     * 创建新的 FTO 分析任务
     * @param data 创建任务请求参数
     * @returns 创建任务响应
     */
    async createTask(data: CreateTaskRequest): Promise<CreateTaskResponse> {
        const result = await this.post<PatSnapSuccessResponseData<CreateTaskResponse>>({
            url: 'fto/submit',
            data,
        });
        return result as unknown as CreateTaskResponse;
    }
    async getStreamBaseUrl() {
        const login = await isUserLogin();
        if (login) {
            return this.options.baseURL;
        }
        return this.options.baseURL?.replace('/core-telecom-api/', '/public-share/');
    }

    /**
     * 获取对话流式消息
     * @param data 对话流式请求参数
     * @returns SSE流对象
     */
    async getDialogueStream(data: DialogueStreamRequest): Promise<PsHttpSSEOfficial<FtoSSEMessage<any>>> {
        const baseUrl = await this.getStreamBaseUrl();
        return this._factorySsePsHttp<FtoSSEMessage>().init({
            url: `${baseUrl}/fto/${data.taskId}/messages`,
            data: {
                replay: data.replay || true,
                delay: 0,
            },
            hasFinalMessage: () => true,
        });
    }

    async saveProject(data: FtoProjectRequest): Promise<FtoProjectResponse> {
        const result = await this.post<PatSnapSuccessResponseData<FtoProjectResponse>>({
            url: `fto/${data.task_id}/project`,
            data,
        });
        return result.data;
    }

    async getProject(taskId: string): Promise<FtoProjectResponse> {
        const result = await this.get<PatSnapSuccessResponseData<FtoProjectResponse>>({
            url: `fto/${taskId}/project`,
        });
        const data = result.data as any;
        // data.retrieval_queries = data.retrieval_queries?.map((item: any) => {
        //     item.query_display = item.query;
        //     item.query_original = item.actual_query;
        //     item.count = item.result_count;
        //     return item;
        // }) || [];
        // data.extract_feature = data.feature_extraction.data;
        // const expandedKeywords = data.expanded_keywords;
        // data.expanded_keywords= {
        //     tech_features:expandedKeywords,
        // };
        return data;
    }

    /**
     * 获取检索式列表
     * @param taskId 任务ID
     * @returns 检索式列表响应
     */
    async getRetrievalQueries(taskId: string): Promise<RetrievalQueriesResponse> {
        const result = await this.get<PatSnapSuccessResponseData<RetrievalQueriesResponse>>({
            url: `fto/${taskId}/retrieval-queries`,
        });
        return result.data;
    }

    /**
     * 获取专利列表
     * @param data 专利列表查询请求参数
     * @returns 专利列表响应结果
     */
    async getPatentList(data: PatentListRequest): Promise<PatentListResponse> {
        const result = await this.post<PatSnapSuccessResponseData<PatentListResponse>>({
            url: 'search-forward/patents',
            data,
            baseURL: coreTelecomApiUrlPrefix,
            cache: true,
        });
        return result.data;
    }

    async getPatentCount(data: PatentListRequest): Promise<PatentCountResponse> {
        const result = await this.post<PatSnapSuccessResponseData<PatentCountResponse>>({
            url: 'patent/query/count',
            data,
            baseURL: coreSearchApiUrlPrefix,
        });
        return result.data;
    }

    async getSemanticRankingPatents(
        data: SemanticRankingPatentPageRequest,
    ): Promise<SemanticRankingPatentPageResponse> {
        const result = await this.get<
            PatSnapSuccessResponseData<SemanticRankingPatentPageResponse>
        >({
            url: `fto/${data.task_id}/screening/r2/patents`,
            params: {
                result_ref: data.result_ref,
                selection: data.selection || 'all',
                patent_id: data.patent_id,
                page: data.page || 1,
                page_size: data.page_size || 20,
            },
        });
        return result.data;
    }

    async getTechnicalTopicThemes(
        data: TechnicalTopicThemeListRequest,
    ): Promise<TechnicalTopicThemeListResponse> {
        const result = await this.get<
            PatSnapSuccessResponseData<TechnicalTopicThemeListResponse>
        >({
            url: `fto/${data.task_id}/screening/r3/themes`,
            params: {
                result_ref: data.result_ref,
                patent_id: data.patent_id,
            },
        });
        return result.data;
    }

    async getTechnicalTopicPatents(
        data: TechnicalTopicPatentPageRequest,
    ): Promise<TechnicalTopicPatentPageResponse> {
        const result = await this.get<
            PatSnapSuccessResponseData<TechnicalTopicPatentPageResponse>
        >({
            url: `fto/${data.task_id}/screening/r3/themes/${data.cluster_id}/patents`,
            params: {
                result_ref: data.result_ref,
                selection: data.selection || 'all',
                patent_id: data.patent_id,
                page: data.page || 1,
                page_size: data.page_size || 20,
            },
        });
        return result.data;
    }

    /**
     * 抽取技术方案特征
     * @param data 特征抽取请求参数
     * @returns FTO分析结果
     */
    async extractFeature(data: ExtractFeatureRequest): Promise<ExtractFeatureResult> {
        const result = await this.post<PatSnapSuccessResponseData<ExtractFeatureResult>>({
            url: 'patent-search/extract',
            data,
        });
        return result as unknown as ExtractFeatureResult;
    }

    /**
     * 生成专利检索查询语句
     * @param data 专利搜索工具请求参数
     * @returns 生成的检索式
     */
    async getSearchQuery(data: PatentSearchToolRequest): Promise<string> {
        const result = await this.post<PatSnapSuccessResponseData<string>>({
            url: 'patent-search/search-query',
            data,
        });
        return result.data;
    }

    /**
     * 执行专利搜索
     * @param data 专利搜索工具请求参数
     * @returns 专利搜索响应结果
     */
    async search(data: PatentSearchToolRequest): Promise<PatentSearchResponse> {
        const result = await this.post<PatSnapSuccessResponseData<PatentSearchResponse>>({
            url: 'patent-search/search',
            data,
        });
        return result.data;
    }

    /**
     * 用户自定义检索
     * @param data 自定义检索请求参数
     * @returns 专利搜索响应结果
     */
    async customSearch(data: CustomSearchRequest): Promise<PatentSearchResponse> {
        const result = await this.post<PatSnapSuccessResponseData<PatentSearchResponse>>({
            url: 'patent-search/custom-search',
            data,
        });
        return result.data;
    }

    /**
     * 执行专利过滤
     * @param data 专利过滤工具请求参数
     * @returns 专利过滤上下文
     */
    async filter(data: PatentFilterToolRequest): Promise<PatentFilterContext> {
        const result = await this.post<PatSnapSuccessResponseData<PatentFilterContext>>({
            url: 'patent-search/filter',
            data,
        });
        return result.data;
    }

    /**
     * 获取最终检索结果
     * @param data 基础请求参数
     * @returns 包含权利要求对比分析的最终检索结果
     */
    async getFinalResult(data: ExtractFeatureRequest): Promise<PatentFinalResultWithClaimAnalysis> {
        const result = await this.post<PatSnapSuccessResponseData<PatentFinalResultWithClaimAnalysis>>({
            url: 'patent-search/final-result',
            data,
        });
        return result.data;
    }

    /**
     * 分析指定专利
     * @param data 专利搜索分析请求参数
     * @returns 分析结果
     */
    async analyzePatent(data: PatentSearchAnalyzeRequest): Promise<ClaimAnalysisResult> {
        const result = await this.post<PatSnapSuccessResponseData<ClaimAnalysisResult>>({
            url: 'patent-search/analyze',
            data,
        });
        return result.data;
    }

    /**
     * 获取下一步检索类型
     * @param data 获取下一步检索类型请求参数
     * @returns 下一步检索类型响应结果
     */
    async getNextStep(data: NextStepRequest): Promise<NextStepResponse> {
        const result = await this.post<PatSnapSuccessResponseData<NextStepResponse>>({
            url: 'patent-search/next-step',
            data,
        });
        return result.data;
    }

    /**
     * 获取拓展关键词
     * @param data 关键词拓展请求参数
     * @returns 关键词拓展响应结果
     */
    async expandedKeywords(data: ExpandedKeywordsRequest): Promise<ExpandedKeywordsResponse> {
        const result = await this.post<PatSnapSuccessResponseData<ExpandedKeywordsResponse>>({
            url: 'patent-search/expanded-keywords',
            data,
        });
        return result.data;
    }

    /**
     * 批量关键词补齐
     * @param data 批量关键词补齐请求参数
     * @returns 批量关键词补齐响应结果
     */
    async completeKeywords(data: CompleteKeywordsRequest): Promise<CompleteKeywordsResponseData> {
        const result = await this.post<PatSnapSuccessResponseData<CompleteKeywordsResponseData>>({
            url: 'fto/keywords/complete',
            data,
        });
        return result.data;
    }

    /**
     * 创建报告
     * @returns 创建报告响应结果
     */
    async createReport(taskId: string): Promise<FtoReportResponse> {
        const result = await this.post<PatSnapSuccessResponseData<FtoReportResponse>>({
            url: `fto/${taskId}/report`,
            data:{},
        });
        return result.data;
    }

    /**
     * 获取报告列表
     * @param data 报告列表查询请求参数
     * @returns 报告列表响应结果
     */
    async getReportList(data: FtoReportListRequest): Promise<FtoReportListResponse> {
        const result = await this.get<PatSnapSuccessResponseData<FtoReportListResponse>>({
            url: `fto/${data.task_id}/reports`,
            params: {
                page: data.page,
                page_size: data.page_size,
            },
        });
        return result.data;
    }

    /**
     * 获取报告详情
     * @param reportId 报告ID
     * @returns 报告详情响应结果
     */
    async getReport(reportId: string): Promise<FtoReportResponse> {
        const result = await this.get<PatSnapSuccessResponseData<FtoReportResponse>>({
            url: `fto/report/${reportId}`,
        });
        return result.data;
    }

    /**
     * FTO 打分接口
     * @param data FTO打分请求参数
     * @returns FTO打分响应结果
     */
    async ftoScore(data: FTOScoreRequest): Promise<FTOScoreResponse> {
        const result = await this.post<PatSnapSuccessResponseData<FTOScoreResponse>>({
            url: 'fto/score',
            data,
        });
        return result.data;
    }

    /**
     * 检查任务是否存在
     * @param data 任务存在性检查请求参数
     * @returns 任务存在性检查响应结果
     */
    async taskExists(data: TaskExistsRequest): Promise<TaskExistsResponse> {
        const result = await this.get<PatSnapSuccessResponseData<TaskExistsResponse>>({
            url: `fto/${data.task_id}/exists`,
        });
        return result as unknown as TaskExistsResponse;
    }

    /**
     * 从工作区导入专利数据
     * @param taskId 任务ID
     * @param folderId 工作区文件夹ID
     * @returns 导入操作响应结果
     */
    async importFromWs(taskId: string, folderId: string, signal?: AbortSignal): Promise<ImportFromWorkspaceResponse> {
        const result = await this.post<PatSnapSuccessResponseData<ImportFromWorkspaceResponse>>({
            url: `fto/${taskId}/cc-from-workspace`,
            data: {
                folder_id: folderId,
            },
            signal,
        });
        return result.data as ImportFromWorkspaceResponse;
    }

    /**
     * 检查工作区导入任务状态
     * @param taskId 任务ID
     * @param batchId 批次ID
     * @returns 导入状态检查响应结果
     */
    async importFromWsStatusCheck(taskId: string, batchId: string, signal?: AbortSignal): Promise<ImportFromWorkspaceResponse> {
        const result = await this.get<PatSnapSuccessResponseData<ImportFromWorkspaceResponse>>({
            url: `fto/${taskId}/cc-status/${batchId}`,
            signal,
        });
        return result.data as ImportFromWorkspaceResponse;
    }

    /**
     * 更新工作区信息
     * @param taskId 任务ID
     * @param data 工作区信息数据
     * @returns 更新操作响应结果
     */
    async updateWorkspaceInfo(taskId: string, data: WorkspaceInfo): Promise<any> {
        const result = await this.post<PatSnapSuccessResponseData<any>>({
            url: `fto/${taskId}/workspace-info`,
            data,
        });
        return result.data;
    }

    /**
     * 获取工作区信息
     * @param taskId 任务ID
     * @returns 工作区信息响应结果
     */
    async getWorkspaceInfo(taskId: string): Promise<WorkspaceInfoResponse> {
        const result = await this.get<PatSnapSuccessResponseData<WorkspaceInfoResponse>>({
            url: `fto/${taskId}/workspace-info`,
        });
        return result.data as WorkspaceInfoResponse;
    }

    /**
     * 保存权利要求分析结果
     * @param taskId 任务ID
     * @param patentId 专利ID
     * @param analysis 权利要求分析结果
     */
    async updateClaimAnalysisResult(taskId: string, patentId: string, analysis: ClaimAnalysisResult): Promise<any> {
        const res = await this.put<PatSnapSuccessResponseData>({
            url: `fto/${taskId}/patent/${patentId}/claim-analysis-results`,
            data: analysis,
        });

        return res.data;
    }

    async getClaimAnalysisResults(taskId: string) {
        const res = await this.get<PatSnapSuccessResponseData>({
            url: `fto/${taskId}/claim-analysis-results`,
        });
        return res?.data as ClaimAnalysisResult[] || [];
    }

    async processAddingPatent(taskId: string, request: ProcessAddingPatentRequest): Promise<ImportFromWorkspaceResponse> {
        const res = await this.post<PatSnapSuccessResponseData<ImportFromWorkspaceResponse>>({
            url: `fto/${taskId}/cc-patent`,
            data: request,
        });
        return res.data as ImportFromWorkspaceResponse;
    }

    async exportReportAsWord(reportId: string): Promise<ExportReportAsWordResponse> {
        const res = await this.get<PatSnapSuccessResponseData<ExportReportAsWordResponse>>({
            url: `fto/report/${reportId}/export/word`,
            timeout: 10 * 60 * 1000, // 10分钟
        });
        return res.data as ExportReportAsWordResponse;
    }

    /**
     * 取消分析任务
     * @param data 取消任务请求参数
     * @returns 取消任务响应结果
     */
    async cancelTask(data: CancelTaskRequest): Promise<CancelTaskResponse> {
        const res = await this.post<PatSnapSuccessResponseData<CancelTaskResponse>>({
            url: 'fto/background/cancel',
            data,
        });
        return res.data as CancelTaskResponse;
    }

    /**
     * 继续分析任务
     * @param taskId 任务ID
     * @param data 恢复任务请求参数（可选）
     * @returns 继续任务响应结果
     */
    async resumeTask(taskId: string, data?: ResumeTaskRequest): Promise<ResumeTaskResponse> {
        const res = await this.post<PatSnapSuccessResponseData<ResumeTaskResponse>>({
            url: `fto/${taskId}/resume`,
            data: data || {},
        });
        return res as unknown as ResumeTaskResponse;
    }

    /**
     * 获取分类助手分区列表
     * @param type 分类类型，如 CPC、IPC 等
     * @returns 分类助手分区列表响应结果
     */
    async getHelperSection(type: string): Promise<HelperSectionResponse> {
        const result = await this.get<PatSnapSuccessResponseData<{ response: HelperSectionResponse }>>({
            url: 'ai-specification/classification/helper-section',
            params: {
                type,
            },
            cache: true,
        });
        return (result.data?.response || []) as unknown as HelperSectionResponse;
    }

    /**
     * 获取分类助手子分类列表
     * @param data 子分类请求参数
     * @returns 子分类列表响应结果
     */
    async getHelperSubclass(data: HelperSubclassRequest): Promise<HelperSubclassResponse> {
        if (data.level <= 1) {
            return this.getHelperSection(data.type);
        }
        const result = await this.get<PatSnapSuccessResponseData<{ response: HelperSubclassResponse }>>({
            url: 'ai-specification/classification/helper-subclass',
            params: {
                type: data.type,
                code: data.code,
                level: data.level,
                start: data.start || 0,
            },
            cache: true,
        });
        return (result.data?.response || []) as unknown as HelperSubclassResponse;
    }

    /**
     * 搜索分类助手
     * @param data 搜索请求参数
     * @returns 搜索结果响应
     */
    async searchHelper(data: HelperSearchRequest): Promise<HelperSearchResponse> {
        const result = await this.get<PatSnapSuccessResponseData<{ response: HelperSearchResponse }>>({
            url: 'ai-specification/classification/helper-search',
            params: {
                q: data.q,
                type: data.type,
                start: data.start || 0,
            },
            cache: true,
        });
        return (result.data?.response || { query_results: [], h_words: [] }) as unknown as HelperSearchResponse;
    }

    /**
     * 上传图片文件
     * @param files 图片文件列表
     * @param onProgress 可选的上传进度回调函数
     * @returns 上传结果响应，包含文件键和签名URL
     */
    async uploadImages(files: File[], onProgress?: UploadProgressCallback): Promise<UploadImagesResponse> {
        // 创建 FormData 对象
        const formData = new FormData();

        // 将所有文件添加到 FormData 中
        files.forEach((file) => {
            formData.append('files', file);
        });

        // 发送 POST 请求
        const result = await this.post<PatSnapSuccessResponseData<UploadImagesResponse>>({
            url: 'fto/upload-images',
            data: formData,
            headers: {
                'Content-Type': 'multipart/form-data',
            },
            onUploadProgress: onProgress ? (progressEvent) => {
                const { loaded, total } = progressEvent;
                if (total) {
                    const percentage = ((loaded * 100) / total).toFixed(2);
                    onProgress({
                        loaded,
                        total,
                        percentage,
                    });
                }
            } : undefined,
        });

        return result.data;
    }

    async getFolderPatents(folderId: string, params?: WorkspaceFolderPatentsRequest): Promise<string[]> {
        const result = await this.post<PatSnapSuccessResponseData<{ folder_id: string; patent_ids: string[]; total: number }>>({
            url: `fto/workspace/folder/${folderId}/patents`,
            data: params || {},
        });
        return result.data?.patent_ids || [];
    }

    async signFiles(keys: string[]): Promise<SignFilesResponse> {
        const result = await this.post<PatSnapSuccessResponseData<SignFilesResponse>>({
            url: 'image-upload/sign',
            data: {
                s3_keys: keys,
            },
            cache: true,
        });

        return result?.data as unknown as SignFilesResponse;
    }
}
